import _ from 'underscore';

import StrUtil from 'util/strUtil';

import {TreeItem} from './SelectTreeConstants';

/**
 * @param selectTreeItems An array of selectTreeItems.
 * @param query An optional string by which to filter the selectTreeItems.
 * @returns An object containing:
 * - filteredItems A filtered set of selectTreeItems by the following rules:
 *    0) If no query is defined, no filtering occurs.
 *    1) A "matching" item is an item with a name that matches (case-insensitive) the query.
 *    2) All children of "matching" items are included.
 * - idsToExpand A list of the immediate parent IDs of the relevant items that should be expanded.
 */
export function getFilteredSelectTreeItems(
  selectTreeItems: Array<TreeItem>,
  query?: string
): {
  filteredItems: Array<TreeItem>;
  idsToExpand: Array<string>;
} {
  if (!query) {
    return {
      filteredItems: selectTreeItems,
      idsToExpand: [],
    };
  }

  const queryMatches = selectTreeItems
    .filter((item) => StrUtil.caseInsensitiveContains(item.name, query))
    .map((item) => item.id);

  if (!queryMatches) {
    return {filteredItems: [], idsToExpand: []};
  }

  const itemsById = _.indexBy(selectTreeItems, 'id');

  const itemsAndChildren = collectDescendentIds(queryMatches, _.groupBy(selectTreeItems, 'parentId'));
  const ancestors = collectAncestorIds(queryMatches, itemsById);
  ancestors.forEach((id) => itemsAndChildren.add(id));
  const allItems = Array.from(itemsAndChildren).map((id) => itemsById[id]);

  return {
    filteredItems: allItems,
    idsToExpand: _getImmediateParentIds(queryMatches, itemsById),
  };
}

function collectDescendentIds(
  itemIds: Array<string>,
  itemsByParentId: _.Dictionary<Array<TreeItem>>
): Set<string> {
  const results = new Set<string>();
  let childIdsToInclude = itemIds.slice(0);
  while (childIdsToInclude.length) {
    const id = childIdsToInclude.pop()!;
    results.add(id);

    const children = itemsByParentId[id] || [];
    if (children.length > 0) {
      childIdsToInclude = childIdsToInclude.concat(children.map((child) => child.id));
    }
  }
  return results;
}

function collectAncestorIds(itemIds: Array<string>, itemsById: _.Dictionary<TreeItem>): Set<string> {
  const results = new Set<string>();
  itemIds.forEach((matchingId) => {
    let item = itemsById[matchingId];
    let parentId = item.parentId;
    while (parentId) {
      item = itemsById[parentId];
      results.add(item.id);
      parentId = item.parentId;
    }
  });
  return results;
}

function _getImmediateParentIds(itemIds: Array<string>, itemsById: _.Dictionary<TreeItem>): Array<string> {
  return _.compact(itemIds.map((itemId) => itemsById[itemId].parentId));
}
